import { ScalarConsumer, ScalarProvider, Scalar } from "./Scalar";

export type Stategroup = { tag: string; value: unknown };

export class StateSwitch<InType extends Stategroup, OutType>
	extends ScalarProvider<OutType>
	implements ScalarConsumer<InType>
{
	private _subscriber: ScalarConsumer<OutType> | undefined;
	private _value: { tag: InType["tag"]; expression: Scalar<OutType> } | undefined;

	constructor(
		readonly source: ScalarProvider<InType>,
		private readonly states: {
			[key in InType["tag"]]: ($: Extract<InType, { tag: key }>["value"]) => Scalar<OutType>;
		},
		readonly __origin?: string
	) {
		super();
	}

	private evaluate(value: InType): Scalar<OutType> {
		return this.states[value.tag as InType["tag"]](value.value);
	}

	get value() {
		let value: Scalar<OutType>;
		if (this._subscriber === undefined) {
			value = this.evaluate(this.source.value);
		} else {
			value = this._value!.expression;
		}
		return value instanceof ScalarProvider ? value.value : value;
	}

	update(value: InType) {
		/* TODO: because the ts api allows resetting the same state with different value this check cannot yet be enabled */
		// if (this._value?.tag === value.tag) return;
		if (this._value?.expression instanceof ScalarProvider) {
			this._value.expression.unsubscribe(this._subscriber!);
		}
		this._value = { tag: value.tag, expression: this.evaluate(value) };
		if (this._subscriber === undefined) return; // Guard against unsubscribing during evaluation
		if (this._value.expression instanceof ScalarProvider) {
			this._value.expression.subscribe(this._subscriber!);
		} else {
			this._subscriber!.update(this._value.expression);
		}
	}

	subscribe(subscriber: ScalarConsumer<OutType>): typeof subscriber {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		this.source.subscribe(this);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<OutType>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		this.source.unsubscribe(this);
		if (this._value?.expression instanceof ScalarProvider) {
			this._value.expression.unsubscribe(subscriber);
		}
		this._value = undefined;
	}
}
